import { useClickOutside } from "@/hooks/useClickOutside";
import { cn } from "@/utils";
import { Check, ChevronDown } from "lucide-react";
import { forwardRef, useCallback, useId, useRef, useState } from "react";

export interface MultiSelectOption {
  value: string;
  label: string;
}

interface MultiSelectDropdownProps extends Omit<React.ComponentPropsWithRef<"div">, "onChange"> {
  values: string[];
  onChange: (values: string[]) => void;
  options: MultiSelectOption[];
  disabled?: boolean;
  placeholder?: string;
  size?: "sm" | "md";
  /**
   * Optional label formatter for the trigger when more than one option is
   * selected. Receives the number of selected items. Default: "N seleccionadas".
   */
  selectedSummaryLabel?: (count: number) => string;
}

/**
 * Multi-select sibling of Dropdown — same visual shell (trigger button +
 * popover panel) but the panel stays open across selections and each option
 * carries a checkbox-style affordance. Used for filter rows where the user
 * picks a subset (e.g. fiscal_category).
 */
export const MultiSelectDropdown = forwardRef<HTMLDivElement, MultiSelectDropdownProps>(
  function MultiSelectDropdown(
    {
      values,
      onChange,
      options,
      disabled = false,
      placeholder = "Todas",
      className,
      size = "md",
      selectedSummaryLabel,
      ...props
    },
    ref,
  ) {
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const listboxId = useId();

    const handleClose = useCallback(() => setIsOpen(false), []);
    useClickOutside(containerRef, handleClose, isOpen);

    const isSm = size === "sm";
    const triggerSizeClasses = isSm ? "px-2 py-1 text-xs" : "px-3 py-2 text-sm";
    const optionSizeClasses = isSm ? "px-2 py-1.5 text-xs" : "px-3 py-2 text-sm";
    const chevronSize = isSm ? 12 : 14;
    const checkSize = isSm ? 12 : 14;
    const maxHeight = isSm ? "max-h-48" : "max-h-60";

    const selectedSet = new Set(values);
    const selectedCount = values.length;
    const triggerLabel = (() => {
      if (selectedCount === 0) return placeholder;
      if (selectedCount === 1) {
        const opt = options.find((o) => o.value === values[0]);
        return opt?.label ?? values[0];
      }
      return selectedSummaryLabel
        ? selectedSummaryLabel(selectedCount)
        : `${selectedCount} seleccionadas`;
    })();

    const handleToggle = (value: string) => {
      const next = new Set(selectedSet);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      onChange(Array.from(next));
    };

    return (
      <div className={cn("relative", className)} ref={ref ?? containerRef} {...props}>
        <button
          type="button"
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-controls={listboxId}
          className={cn(
            "w-full text-left border border-border-secondary rounded-md focus:outline-none focus:ring-1 focus:ring-ring-focus focus:border-transparent disabled:bg-bg-secondary disabled:text-text-tertiary flex items-center justify-between transition-colors",
            triggerSizeClasses,
            selectedCount > 0
              ? "bg-bg-secondary hover:bg-bg-secondary"
              : "bg-bg-primary hover:bg-bg-secondary",
          )}
        >
          <span className={cn(selectedCount > 0 ? "text-text-primary" : "text-text-tertiary")}>
            {triggerLabel}
          </span>
          <ChevronDown
            size={chevronSize}
            className={cn(
              "text-text-tertiary transition-transform duration-200",
              isOpen && "rotate-180",
            )}
          />
        </button>

        {isOpen && !disabled && (
          <div
            id={listboxId}
            role="listbox"
            aria-multiselectable="true"
            className={cn(
              "absolute z-popover w-full mt-1 bg-bg-primary border border-border-secondary rounded-md shadow-lg overflow-auto",
              maxHeight,
            )}
          >
            {options.length === 0 ? (
              <div className={cn("text-text-tertiary", optionSizeClasses, "text-center")}>
                Sin opciones
              </div>
            ) : (
              options.map((option) => {
                const isSelected = selectedSet.has(option.value);
                return (
                  <button
                    type="button"
                    key={option.value}
                    role="option"
                    aria-selected={isSelected}
                    onClick={() => handleToggle(option.value)}
                    className={cn(
                      "w-full flex items-center gap-2 text-left transition-colors",
                      optionSizeClasses,
                      isSelected
                        ? "bg-bg-secondary text-text-primary"
                        : "text-text-secondary hover:bg-bg-secondary",
                    )}
                  >
                    <span
                      className={cn(
                        "flex h-4 w-4 items-center justify-center rounded border transition-colors flex-shrink-0",
                        isSelected
                          ? "bg-primary border-primary text-text-inverse"
                          : "border-border-secondary",
                      )}
                    >
                      {isSelected && <Check size={checkSize} strokeWidth={3} />}
                    </span>
                    <span>{option.label}</span>
                  </button>
                );
              })
            )}
          </div>
        )}
      </div>
    );
  },
);

export default MultiSelectDropdown;
